import classNames from 'classnames';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

import {Text} from 'components/Text/Text';

import styles from './Switch.module.css';

export enum SwitchTheme {
    Light = 'Light',
    Dark = 'Dark',
}
const THEME_CLASS_NAMES = {
    [SwitchTheme.Dark]: styles.dark,
    [SwitchTheme.Light]: styles.light,
};

type SwitchItem = {
    label: string;
    id: string;
};

type Props = {
    onChange: (switchId: string) => void;
    selectedSwitchId: string;
    switches: SwitchItem[];
    className?: string;
    classNameItem?: string;
    theme?: SwitchTheme;
};

export const Switch: FC<Props> = ({
    onChange,
    selectedSwitchId,
    switches,
    className,
    classNameItem,
    theme = SwitchTheme.Light,
}) => {
    const [activeSwitchIndex, setActiveSwitchIndex] = useState(0);
    const [slideWidth, setSlideWidth] = useState(0);
    const [slideLeftPosition, setSlideLeftPosition] = useState(0);

    const makeHandler = useCallback(
        (switchId: string, switchIndex: number) => () => {
            onChange(switchId);
            setActiveSwitchIndex(switchIndex);
        },
        [onChange],
    );

    const switchesRef = useRef<Array<HTMLElement | null>>([]);

    useEffect(() => {
        function setSwitchPosition() {
            const currentSwitch = switchesRef.current[activeSwitchIndex];
            const left = currentSwitch?.offsetLeft
                ? currentSwitch.offsetLeft - 2
                : 0;

            setSlideLeftPosition(left);
            setSlideWidth(currentSwitch?.clientWidth ?? 0);
        }

        setSwitchPosition();
        window.addEventListener('resize', setSwitchPosition);

        return () => window.removeEventListener('resize', setSwitchPosition);
    }, [activeSwitchIndex, switches]);

    const themeClassNames = THEME_CLASS_NAMES[theme];

    return (
        <div className={classNames(styles.block, className, themeClassNames)}>
            {switches.map((item, idx) => {
                return (
                    <div
                        key={item.id}
                        ref={(el) => (switchesRef.current[idx] = el)}
                        className={classNames(styles.item, classNameItem, {
                            [styles.active]: selectedSwitchId === item.id,
                        })}
                        onClick={makeHandler(item.id, idx)}
                    >
                        <Text.Semibold14>{item.label}</Text.Semibold14>
                    </div>
                );
            })}
            <div
                className={classNames(styles.slide)}
                style={{
                    transform: `translateX(${slideLeftPosition}px)`,
                    width: slideWidth,
                }}
            />
        </div>
    );
};
